/**
 * Translation Management Types
 * Types for the BackOffice translation management feature
 */

// ============================================================================
// Enums
// ============================================================================

export enum ApplicationType {
  EvHomeFlutter = 1,
  EnergyAndroidCharger = 2,
  EnergyAndroidKiosk = 3,
  WeezmoWeb = 4,
  WeezmoPdf = 5,
  FlutterWebApp = 6,
  ReactWeb = 7
}

export const ApplicationTypeLabels: Record<ApplicationType, string> = {
  [ApplicationType.EvHomeFlutter]: 'EV OCPP App',
  [ApplicationType.EnergyAndroidCharger]: 'Energy Android Charger',
  [ApplicationType.EnergyAndroidKiosk]: 'Energy Android Kiosk',
  [ApplicationType.WeezmoWeb]: 'Weezmo Web',
  [ApplicationType.WeezmoPdf]: 'Weezmo PDF',
  [ApplicationType.FlutterWebApp]: 'Flutter Web App',
  [ApplicationType.ReactWeb]: 'React Web'
};

export enum S3Environment {
  QA = 1,
  Production = 2
}

export const S3EnvironmentLabels: Record<S3Environment, string> = {
  [S3Environment.QA]: 'QA',
  [S3Environment.Production]: 'Production'
};

export enum MediaType {
  Icon = 1,
  Image = 2,
  Video = 3,
  Sound = 4,
  WeezmoJsFile = 5,
}

export const MediaTypeLabels: Record<MediaType, string> = {
  [MediaType.Icon]: 'Icon',
  [MediaType.Image]: 'Image',
  [MediaType.Video]: 'Video',
  [MediaType.Sound]: 'Sound',
  [MediaType.WeezmoJsFile]: 'Weezmo JS File',
};

// ============================================================================
// Core Interfaces
// ============================================================================

export interface TranslationValue {
  translationValueId: number;
  languageCode: string;
  value: string;
  updated: string;
  updatedByName?: string;
}

export interface TranslationKey {
  translationKeyId: number;
  keyName: string;
  applicationType: ApplicationType;
  description?: string;
  isActive: boolean;
  created: string;
  updated: string;
  createdByName?: string;
  updatedByName?: string;
  values: TranslationValue[];
}

export interface Language {
  languageId: number;
  languageCode: string;
  languageName: string;
  nativeName: string;
  isRtl: boolean;
  imageUrl?: string;
  sortOrder: number;
  isActive: boolean;
  localFormat?: string;
}

export interface TranslationVersion {
  versionId: number;
  applicationType: ApplicationType;
  environment: S3Environment;
  version: number;
  s3Url: string;
  publishedAt: string;
  publishedByName: string;
  notes?: string;
  keyCount?: number;
  /** JSON snapshot of translations - only present when fetching version detail */
  jsonSnapshot?: string;
}

export interface MediaFile {
  mediaId: number;
  applicationType: ApplicationType;
  mediaType: MediaType;
  fileName: string;
  originalFileName: string;
  s3Url: string;
  contentType: string;
  fileSizeBytes: number;
  languageCode?: string;
  created: string;
  createdByName: string;
}

export interface PublishInfo {
  version: number;
  publishedAt: string;
  publishedBy?: string;
  keyCount?: number;
  notes?: string;
}

export interface MissingTranslation {
  languageCode: string;
  languageName: string;
  count: number;
}

export interface PublishStatus {
  isSuccess: boolean;
  errorMessage?: string;
  applicationType: ApplicationType;
  totalKeys: number;
  languageCount: number;
  missingTranslations: MissingTranslation[];
  lastPublishedQa?: PublishInfo;
  lastPublishedProd?: PublishInfo;
}

// ============================================================================
// Request Types
// ============================================================================

export interface CreateTranslationRequest {
  keyName: string;
  applicationType: ApplicationType;
  description?: string;
  values: { languageCode: string; value: string }[];
}

export interface UpdateTranslationRequest {
  description?: string;
  values: { languageCode: string; value: string }[];
}

export interface CreateLanguageRequest {
  languageCode: string;
  languageName: string;
  nativeName: string;
  isRtl: boolean;
  imageUrl?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface UpdateLanguageRequest {
  languageName?: string;
  nativeName?: string;
  isRtl?: boolean;
  imageUrl?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface PublishRequest {
  environment: S3Environment;
  notes?: string;
}

export interface RollbackRequest {
  versionId: number;
  reason: string;
}

export interface PublishPreview {
  applicationType: ApplicationType;
  environment: S3Environment;
  keysToPublish: number;
  languageCount: number;
  newKeys: number;
  modifiedKeys: number;
  deletedKeys: number;
  lastPublishedVersion?: number;
}

export interface PublishResult {
  isSuccess: boolean;
  errorMessage?: string;
  version: number;
  s3Url?: string;
  keyCount: number;
}

export interface RollbackResult {
  success: boolean;
  newVersion: number;
  rolledBackFromVersion: number;
  rolledBackToVersion: number;
}

export interface CopyLanguageValuesResult {
  isSuccess: boolean;
  errorMessage?: string;
  copiedCount: number;
}

export interface BulkImportValidateResponse {
  keysToUpdate: number;
  keysToAdd: number;
  errors: { row?: number; rowNumber?: number; columnName?: string; keyName?: string | null; message: string }[];
  warnings: { row?: number; rowNumber?: number; message: string }[];
  preview: BulkImportPreviewRow[];
  isValid: boolean;
  totalRows?: number;
  errorCount?: number;
  languagesFound?: string[];
}

export interface BulkImportPreviewRow {
  rowNumber: number;
  keyName: string;
  action: 'add' | 'update' | 'skip';
  values: { languageCode: string; value: string; isNew: boolean }[];
}

export interface ImportFailedKey {
  keyName: string;
  rowNumber?: number; // For Excel imports
  errorMessage: string;
}

/**
 * Language-specific value impact for translation imports
 * Shows exactly how many translation values will be inserted vs updated per language
 */
export interface LanguageValueImpact {
  languageCode: string;      // e.g., "en", "he"
  valuesToInsert: number;    // NEW values being added
  valuesToUpdate: number;    // Existing values being updated
  totalValues: number;       // Insert + Update
}

/**
 * Import status enum for async imports
 * API returns numeric values: 0=Pending, 1=InProgress, 2=Completed, 3=Failed
 */
export enum ImportStatus {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Failed = 3
}

export const ImportStatusLabels: Record<ImportStatus, string> = {
  [ImportStatus.Pending]: 'Pending',
  [ImportStatus.InProgress]: 'In Progress',
  [ImportStatus.Completed]: 'Completed',
  [ImportStatus.Failed]: 'Failed'
};

/**
 * Response when starting an async import (returned immediately)
 */
export interface TranslationImportStartResponse {
  isSuccess: boolean;
  errorMessage?: string;
  importId: string;      // GUID to poll for status
  totalKeys: number;     // Total keys to be processed
  message: string;       // "Import started"
}

/**
 * Per-language import result
 */
export interface LanguageImportResult {
  languageCode: string;    // e.g., "en", "he", "fr"
  keysProcessed: number;   // Keys processed for this language
  valuesUpdated: number;   // Values updated for this language
  isSuccess: boolean;
  errorMessage?: string;
}

/**
 * Response when polling import status
 */
export interface TranslationImportStatusResponse {
  isSuccess: boolean;
  errorMessage?: string;
  importId: string;
  status: ImportStatus;  // 'Pending', 'InProgress', 'Completed', 'Failed'
  totalKeys: number;
  processedKeys: number;
  percentComplete: number;  // 0-100
  keysAdded: number;
  keysUpdated: number;
  keysFailed: number;
  keysDeleted: number;
  valuesUpdated: number;
  failedKeys: ImportFailedKey[];
  durationMs: number;
  // Language tracking fields
  currentLanguage?: string;           // Currently processing language code (e.g., "fr")
  currentLanguageIndex?: number;      // 1-based index (e.g., 3 means "3rd language")
  totalLanguages: number;             // Total languages to process
  completedLanguages: number;         // Languages fully processed
  languagesToProcess?: string[];      // All language codes in order ["en", "he", "fr", ...]
  languageResults: LanguageImportResult[];  // Per-language results
}

/**
 * Language name mapping for display
 */
export const LANGUAGE_NAMES: Record<string, string> = {
  'en': 'English',
  'en-gb': 'English (UK)',
  'he': 'Hebrew',
  'fr': 'French',
  'es': 'Spanish',
  'pt': 'Portuguese',
  'ca': 'Catalan',
  'ru': 'Russian',
  'de': 'German',
  'hu': 'Hungarian',
  'ua': 'Ukrainian',
  'cz': 'Czech',
  'sk': 'Slovak',
  'pl': 'Polish',
  'ar': 'Arabic',
  'ja': 'Japanese',
  'it': 'Italian',
  'th': 'Thai',
  'bg': 'Bulgarian',
  'sv': 'Swedish',
  'nb': 'Norwegian',
  'el': 'Greek',
  'ge': 'Georgian',
  'nl': 'Dutch',
  'fi': 'Finnish',
  'da': 'Danish',
  'tr': 'Turkish',
  'ko': 'Korean',
  'zh': 'Chinese',
  'vi': 'Vietnamese',
  'id': 'Indonesian',
  'ms': 'Malay',
  'ro': 'Romanian',
  'hr': 'Croatian',
  'sr': 'Serbian',
  'sl': 'Slovenian',
  'et': 'Estonian',
  'lv': 'Latvian',
  'lt': 'Lithuanian',
};

export const getLanguageName = (code: string): string => {
  const lowerCode = code.toLowerCase();
  return LANGUAGE_NAMES[lowerCode] || code.toUpperCase();
};

export interface BulkImportResult {
  success?: boolean; // Legacy field
  isSuccess: boolean; // Actual API field
  keysAdded: number;
  keysUpdated: number;
  keysSkipped?: number;
  keysDeleted?: number;
  keysFailed?: number; // NEW - count of failed keys
  failedKeys?: ImportFailedKey[]; // NEW - details of failed keys
  valuesUpdated?: number;
  durationMs?: number;
  requestId?: string;
  errorMessage?: string | null;
  errorCode?: string | null;
  exception?: string | null;
  errors?: { row: number; message: string }[];
}

export interface BulkImportRequest {
  applicationType: ApplicationType;
  mode: ImportMode;
}

export enum ImportMode {
  MergeUpdate = 0,  // Update existing, add new
  AddNewOnly = 1,   // Only add new keys
  ReplaceAll = 2    // Delete existing, import all
}

export const ImportModeLabels: Record<ImportMode, string> = {
  [ImportMode.MergeUpdate]: 'Update existing & Add new',
  [ImportMode.AddNewOnly]: 'Add new only (skip existing)',
  [ImportMode.ReplaceAll]: 'Replace all (delete existing)'
};

export const ImportModeDescriptions: Record<ImportMode, string> = {
  [ImportMode.MergeUpdate]: 'Updates existing keys with new values and adds any new keys from the import file',
  [ImportMode.AddNewOnly]: 'Only adds keys that do not already exist, skips any existing keys',
  [ImportMode.ReplaceAll]: 'Deletes all existing translations and imports only what is in the file. Use with caution!'
};

// ============================================================================
// Legacy Import Types
// ============================================================================

/**
 * File type for import operations
 */
export enum ImportFileType {
  Excel = 'excel',
  Json = 'json'
}

export const ImportFileTypeLabels: Record<ImportFileType, string> = {
  [ImportFileType.Excel]: 'Excel (.xlsx)',
  [ImportFileType.Json]: 'JSON (.json)'
};

/**
 * Legacy Excel sheet mappings
 * Multi-sheet Excel file (EV_Products_Translation.xlsx)
 */
export const LegacyExcelSheetMappings: Record<string, ApplicationType> = {
  'Kiosk': ApplicationType.EnergyAndroidKiosk,
  'Charging Station': ApplicationType.EnergyAndroidCharger,
  'Weezmo': ApplicationType.WeezmoWeb,
  'Weezmo PDF': ApplicationType.WeezmoPdf
};

/**
 * Detected JSON format types
 */
export enum JsonFormatType {
  Unknown = 0,
  EvHome = 1,      // { "languages": { "en": { "translation": {...} } }, "version": 71 }
  ReactWeb = 2     // { "TranslationDic": { "key": "value" }, "UpdatedDt": "..." }
}

export const JsonFormatTypeLabels: Record<JsonFormatType, string> = {
  [JsonFormatType.Unknown]: 'Unknown Format',
  [JsonFormatType.EvHome]: 'EV Home Format (Multi-language)',
  [JsonFormatType.ReactWeb]: 'React Web Format (Single-language)'
};

/**
 * Legacy JSON import validation request
 */
export interface LegacyJsonValidateRequest {
  fileContent: string;  // Base64 encoded JSON
  applicationType: ApplicationType;
  languageCode?: string;  // Required for single-language JSON (ReactWeb format)
  targetLanguageCode?: string;  // Optional - import only this language from the file
  fileName?: string;  // Original filename for format detection
}

/**
 * Legacy JSON import request
 */
export interface LegacyJsonImportRequest {
  fileContent: string;  // Base64 encoded JSON
  applicationType: ApplicationType;
  importMode: ImportMode;
  languageCode?: string;  // Required for single-language JSON (ReactWeb format)
  fileName?: string;  // Original filename for format detection
}

/**
 * Legacy Excel import validation request (extended)
 */
export interface LegacyExcelValidateRequest {
  fileContent: string;  // Base64 encoded Excel
  applicationType: ApplicationType;
  sheetName?: string;  // Optional - for multi-sheet Excel files
  targetLanguageCode?: string;  // Optional - import only this language from the file
}

/**
 * Legacy Excel import request (extended)
 */
export interface LegacyExcelImportRequest {
  fileContent: string;  // Base64 encoded Excel
  applicationType: ApplicationType;
  importMode: ImportMode;
  sheetName?: string;  // Optional - for multi-sheet Excel files
  targetLanguageCode?: string;  // Optional - import only this language from the file
}

/**
 * JSON format types (legacy alias - prefer using JsonFormatType enum)
 */
export type JsonFormat = JsonFormatType;

/**
 * Extended validation response for legacy imports
 */
export interface LegacyImportValidateResponse extends BulkImportValidateResponse {
  detectedFormat?: JsonFormatType;  // Detected format (numeric enum)
  expectedFormat?: JsonFormatType;  // Expected format for the application type (JSON only)
  isFormatCompatible?: boolean;  // True if detected format matches expected format (JSON only)
  isMultiLanguage?: boolean;  // True if JSON contains multiple languages
  singleLanguageCode?: string;  // Language code if single-language JSON
  detectedLanguages?: string[];  // Languages found in the file
  detectedSheets?: string[];  // Sheet names for multi-sheet Excel (sheetNames in API)
  sheetNames?: string[];  // Available sheets in Excel file (API field name)
  requiresLanguageSelection?: boolean;  // True for single-language JSON
  requiresSheetSelection?: boolean;  // True for multi-sheet Excel
  // Additional fields from TranslationJsonImportValidateResponse
  newKeys?: number;
  existingKeys?: number;
  valuesImpact?: LanguageValueImpact[];  // Language-specific insert/update breakdown
}

// ============================================================================
// Response Types
// ============================================================================

export interface TranslationsResponse {
  '@odata.count'?: number;
  value: TranslationKey[];
}

export interface LanguagesResponse {
  '@odata.count'?: number;
  value: Language[];
}

export interface VersionsResponse {
  '@odata.count'?: number;
  value: TranslationVersion[];
}

export interface MediaResponse {
  '@odata.count'?: number;
  value: MediaFile[];
}

export interface ExportResponse {
  fileContent: string;  // Base64 encoded file
  fileName: string;
  contentType: string;
  keyCount: number;
  languages: string[];
  requestId: string;
  isSuccess: boolean;
  errorMessage: string | null;
  errorCode: string | null;
  exception: string | null;
}

// ============================================================================
// Query Parameters
// ============================================================================

export interface TranslationQueryParams {
  applicationType: ApplicationType;
  skip?: number;
  top?: number;
  filter?: string;
  orderBy?: string;
}

export interface VersionQueryParams {
  applicationType: ApplicationType;
  environment?: S3Environment;
}

export interface MediaQueryParams {
  applicationType: ApplicationType;
  mediaType?: MediaType;
  languageCode?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get RTL language codes
 */
export const RTL_LANGUAGES = ['he', 'ar', 'fa', 'ur'];

export function isRtlLanguage(languageCode: string): boolean {
  return RTL_LANGUAGES.includes(languageCode.toLowerCase());
}

/**
 * Validate key name format (lowercase + underscores only)
 */
export function isValidKeyName(keyName: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(keyName);
}

/**
 * Get application types that support media
 */
export const MEDIA_SUPPORTED_APP_TYPES = [
  ApplicationType.EnergyAndroidCharger,
  ApplicationType.EnergyAndroidKiosk
];

export function supportsMedia(appType: ApplicationType): boolean {
  return MEDIA_SUPPORTED_APP_TYPES.includes(appType);
}

// ============================================================================
// Preview Types
// ============================================================================

/**
 * Information about a single preview file (for multi-file apps)
 */
export interface PreviewFileInfo {
  fileKey: string;      // Use as query param for preview/download
  fileName: string;     // Display name (e.g., "english.json")
  languageCode: string; // e.g., "en"
  languageName: string; // e.g., "English"
}

/**
 * Response from GET /api/v1/translations/preview/{applicationType}/info
 */
export interface PreviewInfoResponse {
  requestId: string;
  isSuccess: boolean;
  errorMessage?: string;
  applicationType: string;
  isMultiFile: boolean;
  totalKeys: number;
  availableFiles: PreviewFileInfo[];
}

/**
 * Multi-file application types (require fileKey selection)
 */
export const MULTI_FILE_APP_TYPES = [
  ApplicationType.EvHomeFlutter,
  ApplicationType.WeezmoPdf,
  ApplicationType.FlutterWebApp,
  ApplicationType.ReactWeb,
];

export function isMultiFileAppType(appType: ApplicationType): boolean {
  return MULTI_FILE_APP_TYPES.includes(appType);
}
